import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AiConfig, AiConversationDetailDto, AiConversationDto, AiMessageDto } from '@jordan/types';
import {
  Alert,
  type AppLayoutApi,
  Button,
  SecondarySidebar,
  SecondarySidebarSection,
  SelectMenu,
  Spinner,
} from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { apiDelete, apiGet, apiStream } from '../../lib/api';

export function AiChatPage() {
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId?: string }>();

  const [cfg, setCfg] = useState<AiConfig | null>(null);
  const [conversations, setConversations] = useState<AiConversationDto[]>([]);
  const [model, setModel] = useState('');
  const [messages, setMessages] = useState<AiMessageDto[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Which conversation's messages are currently loaded, so we don't refetch after we navigate
  // to a freshly-created chat we already have in state.
  const loadedId = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const refreshConversations = useCallback(
    () => apiGet<AiConversationDto[]>('/api/ai/conversations').then(setConversations).catch(() => undefined),
    [],
  );

  // Config + recents on mount.
  useEffect(() => {
    apiGet<AiConfig>('/api/config/ai')
      .then((c) => {
        setCfg(c);
        setModel((m) => m || c.defaultModel);
      })
      .catch(() => setErr('Failed to load AI settings.'));
    void refreshConversations();
  }, [refreshConversations]);

  // Load (or clear) the conversation when the route id changes.
  useEffect(() => {
    if (!conversationId) {
      loadedId.current = null;
      setMessages([]);
      return;
    }
    if (conversationId === loadedId.current) return; // already in state (just-created chat)
    setErr(null);
    apiGet<AiConversationDetailDto>(`/api/ai/conversations/${conversationId}`)
      .then((d) => {
        loadedId.current = conversationId;
        setMessages(d.messages);
        setModel(d.conversation.model);
      })
      .catch(() => setErr('Could not load that conversation.'));
  }, [conversationId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streaming]);

  const send = async () => {
    const content = input.trim();
    if (!content || streaming || !model) return;
    setErr(null);
    setInput('');
    const now = new Date().toISOString();
    // Optimistic user bubble + an empty assistant bubble we stream into.
    setMessages((m) => [
      ...m,
      { role: 'user', content, createdAt: now },
      { role: 'assistant', content: '', createdAt: now },
    ]);
    setStreaming(true);

    let newId: string | null = null;
    try {
      await apiStream(
        '/api/ai/chat',
        { conversationId: conversationId ?? undefined, model, content },
        {
          onMeta: (meta) => {
            newId = meta.conversationId;
          },
          onDelta: (text) => {
            setMessages((m) => {
              const next = [...m];
              const last = next[next.length - 1];
              if (last?.role === 'assistant') next[next.length - 1] = { ...last, content: last.content + text };
              return next;
            });
          },
          onError: (message) => setErr(message),
        },
      );
    } catch {
      setErr('The chat request failed. Please try again.');
    } finally {
      setStreaming(false);
    }

    void refreshConversations();
    // For a brand-new conversation, adopt the id without triggering a refetch.
    if (!conversationId && newId) {
      loadedId.current = newId;
      navigate(`/ai/${newId}`, { replace: true });
    }
  };

  const remove = async (id: string) => {
    try {
      await apiDelete(`/api/ai/conversations/${id}`);
      setConversations((c) => c.filter((x) => x.id !== id));
      if (id === conversationId) navigate('/ai');
    } catch {
      setErr('Could not delete that conversation.');
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const modelOptions = (cfg?.models ?? [])
    .filter((m) => cfg?.enabledModels.includes(m.id))
    .map((m) => ({ value: m.id, label: m.label }));
  const empty = messages.length === 0;

  const composer = (
    <div className="card border shadow-sm">
      <div className="card-body">
        <textarea
          className="form-control border-0 shadow-none p-0 mb-2"
          rows={empty ? 2 : 3}
          placeholder="How can I help you today?"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={streaming}
          style={{ resize: 'none' }}
        />
        <div className="d-flex align-items-center gap-2">
          <div style={{ minWidth: 200 }}>
            <SelectMenu
              className="mb-0"
              icon="ph-cpu"
              value={model}
              onChange={setModel}
              options={modelOptions}
            />
          </div>
          <Button
            variant="primary"
            className="btn-icon rounded-circle ms-auto"
            icon={streaming ? 'ph-circle-notch' : 'ph-arrow-up'}
            aria-label="Send"
            disabled={!input.trim() || streaming || !model}
            onClick={() => void send()}
          />
        </div>
      </div>
    </div>
  );

  // Secondary sidebar: New chat + Recents.
  const secondarySidebar = (api: AppLayoutApi) => {
    const go = (path: string) => {
      navigate(path);
      api.closeSecondarySidebar();
    };
    return (
      <SecondarySidebar title="Chat" mobileExpanded={api.secondarySidebarExpanded} onClose={api.closeSecondarySidebar}>
        <SecondarySidebarSection title="" flush>
          <div className="p-3">
            <Button variant="light" icon="ph-plus" className="w-100" onClick={() => go('/ai')}>
              New chat
            </Button>
          </div>
        </SecondarySidebarSection>
        <SecondarySidebarSection title="Recents" flush>
          <ul className="nav nav-sidebar my-2" data-nav-type="accordion">
            {conversations.length === 0 && <li className="nav-item-header opacity-50">No conversations yet</li>}
            {conversations.map((c) => (
              <li className="nav-item d-flex align-items-center" key={c.id}>
                <a
                  href={`/ai/${c.id}`}
                  className={`nav-link flex-grow-1 text-truncate ${c.id === conversationId ? 'active' : ''}`.trim()}
                  onClick={(e) => {
                    e.preventDefault();
                    go(`/ai/${c.id}`);
                  }}
                  title={c.title}
                >
                  {c.title}
                </a>
                <button
                  type="button"
                  className="btn btn-sm btn-flat-light text-muted border-transparent me-2"
                  aria-label="Delete conversation"
                  onClick={() => void remove(c.id)}
                >
                  <i className="ph-trash" />
                </button>
              </li>
            ))}
          </ul>
        </SecondarySidebarSection>
      </SecondarySidebar>
    );
  };

  return (
    <AppChrome secondarySidebar={secondarySidebar}>
      <div className="d-flex flex-column" style={{ height: '100%' }}>
        {err && (
          <div className="px-3 pt-3">
            <Alert variant="danger" icon="ph-warning" dismissible>
              {err}
            </Alert>
          </div>
        )}

        {!cfg ? (
          <div className="p-4">
            <Spinner />
          </div>
        ) : empty ? (
          // Centered greeting + composer (new chat)
          <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center px-3">
            <h2 className="fw-bold mb-4 text-center">How can I help you today?</h2>
            <div className="w-100" style={{ maxWidth: 760 }}>
              {composer}
            </div>
          </div>
        ) : (
          // Conversation view
          <>
            <div ref={scrollRef} className="flex-grow-1 overflow-auto px-3 py-3">
              <div className="mx-auto" style={{ maxWidth: 820 }}>
                {messages.map((m, i) => (
                  <div key={i} className={`d-flex mb-3 ${m.role === 'user' ? 'justify-content-end' : ''}`}>
                    <div
                      className={
                        m.role === 'user'
                          ? 'bg-primary text-white rounded-3 px-3 py-2'
                          : 'bg-light rounded-3 px-3 py-2'
                      }
                      style={{ maxWidth: '85%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {m.content || (streaming && i === messages.length - 1 ? <Spinner size="sm" /> : null)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-top px-3 py-3">
              <div className="mx-auto" style={{ maxWidth: 820 }}>
                {composer}
              </div>
            </div>
          </>
        )}
      </div>
    </AppChrome>
  );
}
